import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Container, useMediaQuery, useTheme } from '@mui/material';
import { AuditionListContainer } from '../../components/ScreenContainer/AuditionListContainer/AuditionListContainer';
import { useSelector } from 'react-redux';
import { auditionsSelector, filterSelector, selectedFiltersSelectors } from './selector';
import { useDispatch } from 'react-redux';
import { addFilter, filterAudition, getAllAuditions } from '../../redux/audition/actions';
import { MetaTags } from '../../components/Metatags/Metatags';
import { pickAuditionCreatedTimestamp } from '../../utils/AppUtils';
import { getFilters } from '../../redux/appConfigurator/action';
import { IAuditionFilter } from '../../redux/audition/types';

const parseFilter = (filters: IAuditionFilter) => {
    const parsed: Record<string, any> = {};
    Object.keys(filters).forEach(key => {
        const typedKey = key as keyof IAuditionFilter;
        const { value } = filters[typedKey];
        switch (typedKey) {
            case 'rehearsal_date_end':
            case 'rehearsal_date_start':
            case 'job_start_date':
            case 'job_end_date':
                parsed[key] = value;
                break;
            case 'talent_location':
            case 'shoot_location':
                if (typeof value === 'object' && 'coordinates' in value) {
                    parsed[key] = value.coordinates;
                }
                break;
            default:
                parsed[key] = filters[key as keyof IAuditionFilter].value;
                break;
        }
    });
    return parsed;
};

const Audition = () => {
    const { auditions, page, total, canLoadMore, limit, loading } = useSelector(auditionsSelector);
    const muiTheme = useTheme();
    const filters = useSelector(filterSelector);
    const selectedFilters = useSelector(selectedFiltersSelectors);
    const dispatch = useDispatch();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm')); // Fullscreen on mobile
    const [awaitingFreshAuditions, setAwaitingFreshAuditions] = useState(() => auditions.length === 0);

    const handleOnApply = (payload?: IAuditionFilter) => {
        if (payload) {
            const parsedPayload = parseFilter(payload);
            dispatch(addFilter(payload));
            dispatch(
                filterAudition({
                    ...parsedPayload,
                }),
            );
        }
    };

    const handleClearFilters = () => {
        dispatch(addFilter(undefined));
        dispatch(getAllAuditions(undefined));
    };

    const handleOnEndReach = () => {
        if (canLoadMore && !loading) {
            dispatch(
                getAllAuditions({
                    page: page + 1,
                    limit: limit,
                }),
            );
        }
    };

    useEffect(() => {
        // Always refresh filter config on load; backend owns caching.
        dispatch(getFilters({ coll_type: 'audition' }));
    }, [dispatch]);

    useEffect(() => {
        // Always refresh audition list on load; backend owns caching.
        dispatch(
            getAllAuditions({
                limit: 20,
                page: 1,
            }),
        );
    }, [dispatch]);

    useEffect(() => {
        if (!loading) {
            setAwaitingFreshAuditions(false);
        }
    }, [loading]);

    const seoTitle = 'Auditions & UGC Gigs | Find Acting Jobs & Creator Opportunities - Thspian';
    const seoDescription =
        'Discover auditions, UGC gigs, and casting calls on Thspian. Find acting jobs, content creator opportunities, and paid creative work. Browse open auditions and apply to UGC campaigns from top brands. Join thousands of creators earning money through auditions and UGC opportunities.';
    const seoKeywords =
        'auditions, UGC gigs, casting calls, acting jobs, content creator opportunities, paid creative work, UGC campaigns, brand partnerships, creator economy, audition opportunities, acting auditions, video auditions, commercial auditions, film auditions, UGC creator jobs, earn money as creator, creator opportunities';

    const structuredData = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: seoTitle,
        description: seoDescription,
        url: 'https://thspian.com/audme',
        mainEntity: {
            '@type': 'ItemList',
            itemListElement: (auditions || []).slice(0, 10).map((audition, index) => {
                // Build job location
                const jobLocation = audition.shoot_location || audition.location || audition.work_location;
                const locationData: any = {
                    '@type': 'Place',
                };

                if (jobLocation) {
                    if (jobLocation.description) {
                        locationData.address = {
                            '@type': 'PostalAddress',
                            addressLocality: jobLocation.description,
                            ...(jobLocation.short_name && { addressRegion: jobLocation.short_name }),
                        };
                    } else if (jobLocation.short_name) {
                        locationData.address = {
                            '@type': 'PostalAddress',
                            addressLocality: jobLocation.short_name,
                        };
                    } else {
                        locationData.address = {
                            '@type': 'PostalAddress',
                            addressLocality: 'Location TBD',
                        };
                    }
                } else {
                    locationData.address = {
                        '@type': 'PostalAddress',
                        addressLocality: 'Location TBD',
                    };
                }

                // Build hiring organization
                const hiringOrganization: any = {
                    '@type': 'Organization',
                    name: audition.production_company?.company_name || 'Production Company',
                };

                if (audition.production_company?.company_name) {
                    hiringOrganization.sameAs = audition.production_company.company_name;
                }

                // Build base salary if available
                const baseSalary: any = {};
                if (audition.reward || audition.min_reward || audition.max_reward) {
                    baseSalary['@type'] = 'MonetaryAmount';
                    baseSalary.currency = audition.currency || 'USD';

                    if (audition.min_reward && audition.max_reward) {
                        baseSalary.value = {
                            '@type': 'QuantitativeValue',
                            minValue: audition.min_reward,
                            maxValue: audition.max_reward,
                            unitText: audition.reward_frequency || 'ONE_TIME',
                        };
                    } else if (audition.reward) {
                        baseSalary.value = {
                            '@type': 'QuantitativeValue',
                            value: audition.reward,
                            unitText: audition.reward_frequency || 'ONE_TIME',
                        };
                    } else if (audition.min_reward) {
                        baseSalary.value = {
                            '@type': 'QuantitativeValue',
                            minValue: audition.min_reward,
                            unitText: audition.reward_frequency || 'ONE_TIME',
                        };
                    }
                }

                // Get dates
                const datePosted = pickAuditionCreatedTimestamp(audition);
                const validThrough = audition.casting_expiration || audition.expiration;

                const jobPosting: any = {
                    '@type': 'JobPosting',
                    title: audition.title || 'Audition Opportunity',
                    description:
                        audition.description || audition.synopsis || audition.plot || 'Audition and UGC opportunity',
                    employmentType: audition.employment_type || 'CONTRACTOR',
                    datePosted: datePosted ? new Date(datePosted).toISOString() : new Date().toISOString(),
                    jobLocation: locationData,
                    hiringOrganization: hiringOrganization,
                };

                // Add validThrough only if available
                if (validThrough) {
                    try {
                        jobPosting.validThrough = new Date(validThrough).toISOString();
                    } catch (e) {
                        // Invalid date, skip it
                    }
                }

                // Add baseSalary only if available
                if (Object.keys(baseSalary).length > 0) {
                    jobPosting.baseSalary = baseSalary;
                }

                return {
                    '@type': 'ListItem',
                    position: index + 1,
                    item: jobPosting,
                };
            }),
        },
    };

    return (
        <MetaTags
            title={seoTitle}
            description={seoDescription}
            keywords={seoKeywords}
            canonicalUrl="https://thspian.com/audme"
            structuredData={structuredData}>
            <Container
                disableGutters={isMobile}
                sx={{
                    minWidth: '100%',
                    marginLeft: { xs: '0px', sm: '20px' },
                    marginTop: { xs: 2, sm: 0 },
                    overflowY: 'auto',
                }}>
                {awaitingFreshAuditions ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <AuditionListContainer
                        auditions={auditions}
                        filter={filters}
                        onApply={handleOnApply}
                        selectedFilters={selectedFilters}
                        deleteFilter={handleOnApply}
                        loading={loading}
                        onClearFilter={handleClearFilters}
                        onApplyClick={() => {}}
                        totalResults={total}
                        onCenterEndReached={handleOnEndReach}
                        onRightEndReached={() => {}}
                    />
                )}
            </Container>
        </MetaTags>
    );
};

export { Audition };
